// Hybrid Summarizer - On-device first, cloud fallback
// Uses Chrome Summarizer API for on-device summarization

import { ChromeAIDetector } from './chrome-ai-detector';
import { API_BASE_URL } from '../config/settings';

export interface SummaryOptions {
  type?: 'key-points' | 'tl;dr' | 'teaser' | 'headline';
  length?: 'short' | 'medium' | 'long';
  context?: string;
}

export interface SummaryResult {
  summary: string;
  method: 'on-device' | 'cloud';
  duration: number;
  cost: number;
}

export class HybridSummarizer {
  private static MAX_ON_DEVICE_LENGTH = 10000; // chars

  /**
   * Generate summary (hybrid: on-device first, cloud fallback)
   */
  static async summarize(content: string, options: SummaryOptions = {}): Promise<SummaryResult> {
    const {
      type = 'key-points',
      length = 'short',
      context = ''
    } = options;

    const startTime = Date.now();

    // Try on-device first
    if (await this.canUseOnDevice(content.length)) {
      try {
        const summary = await this.summarizeOnDevice(content, { type, length, context });

        this.trackUsage('on-device', Date.now() - startTime, 0);

        return {
          summary,
          method: 'on-device',
          duration: Date.now() - startTime,
          cost: 0
        };
      } catch (error) {
        console.warn('⚠️ On-device summarization failed, falling back to cloud:', error);
      }
    }

    // Fallback to cloud
    const summary = await this.summarizeCloud(content, { type, length, context });

    this.trackUsage('cloud', Date.now() - startTime, 0.0001);

    return {
      summary,
      method: 'cloud',
      duration: Date.now() - startTime,
      cost: 0.0001
    };
  }

  /**
   * Check if we can use on-device
   */
  private static async canUseOnDevice(contentLength: number): Promise<boolean> {
    // Content too long?
    if (contentLength > this.MAX_ON_DEVICE_LENGTH) {
      return false;
    }

    // Summarizer API available?
    const availability = await ChromeAIDetector.getCached();
    return availability.summarizer?.canUse === true;
  }

  /**
   * Summarize using Chrome Summarizer API (on-device)
   */
  private static async summarizeOnDevice(content: string, options: SummaryOptions): Promise<string> {
    const { type, length, context } = options;

    // @ts-ignore - Chrome AI API
    const summarizer = await self.ai.summarizer.create({
      type: type || 'key-points',
      length: length || 'short',
      format: 'plain-text',
      sharedContext: context || ''
    });

    // Truncate if needed
    const truncated = content.length > this.MAX_ON_DEVICE_LENGTH
      ? content.slice(0, this.MAX_ON_DEVICE_LENGTH)
      : content;

    const summary = await summarizer.summarize(truncated);

    // Cleanup
    summarizer.destroy();

    return summary.trim();
  }

  /**
   * Summarize using Workers AI (cloud fallback)
   */
  private static async summarizeCloud(content: string, options: SummaryOptions): Promise<string> {
    const response = await fetch(`${API_BASE_URL}/ai/summarize`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        content,
        type: options.type,
        length: options.length,
        context: options.context
      })
    });

    if (!response.ok) {
      throw new Error('Cloud summarization failed');
    }

    const data = await response.json();
    return data.summary || data.data?.summary || '';
  }

  /**
   * Track usage statistics
   */
  private static trackUsage(method: 'on-device' | 'cloud', duration: number, cost: number): void {
    const stats = this.getStats();

    if (method === 'on-device') {
      stats.onDevice.count++;
      stats.onDevice.totalDuration += duration;
    } else {
      stats.cloud.count++;
      stats.cloud.totalDuration += duration;
      stats.cloud.totalCost += cost;
    }

    localStorage.setItem('ai_usage_stats', JSON.stringify(stats));
  }

  /**
   * Get usage statistics
   */
  static getStats() {
    const cached = localStorage.getItem('ai_usage_stats');
    const stats = cached ? JSON.parse(cached) : {
      onDevice: { count: 0, totalDuration: 0 },
      cloud: { count: 0, totalDuration: 0, totalCost: 0 }
    };

    const total = stats.onDevice.count + stats.cloud.count;
    const savedCost = stats.onDevice.count * 0.0001;

    return {
      ...stats,
      total,
      onDevicePercentage: total > 0 ? ((stats.onDevice.count / total) * 100).toFixed(1) : '0',
      savedCost: savedCost.toFixed(4)
    };
  }

  /**
   * Clear statistics
   */
  static clearStats(): void {
    localStorage.removeItem('ai_usage_stats');
  }
}
